import { graphql, useStaticQuery } from 'gatsby';
import { includes } from 'ramda';
import React, { FC, useContext } from 'react';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import useMemberInfo from '@hooks/useMemberInfo';

import { keysToCamel } from '@utils/butter';
import { doesNotExist } from '@utils/general';

import FlowContext from '@providers/FlowContextProvider';

import { medicaidStates } from '@data/US_States';

import { IFlowStepProps } from '@folx-types/flow-steps';

import MedicaidAttestationStep, {
	IMedicaidAttestationStepPageContent,
} from './MedicaidAttestationStep.component';

interface IMedicaidAttestationStepPageProps extends IFlowStepProps {}

const FullWidthColumn = fullWidth(FlexColumn);
const AlignedRow = aligned(FlexRow);

const CheckboxSkeleton = styled(Skeleton)`
	flex-shrink: 0;
`;

const AttestationSkeleton = (
	<CenteredGridItem>
		<FullWidthColumn gap="lg">
			<Skeleton structure="column" repeated={2} height="lg" fullWidth />
			<Skeleton structure="column" repeated={4} height="md" fullWidth />
			<AlignedRow gap="sm" alignItems="flex-start">
				<CheckboxSkeleton height="md" width="md" />
				<Skeleton structure="column" repeated={6} height="sm" fullWidth />
			</AlignedRow>
		</FullWidthColumn>
	</CenteredGridItem>
);

const MedicaidAttestationStepPage: FC<IMedicaidAttestationStepPageProps> = ({
	path,
	stepKey,
	...props
}) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "medicaid-attestation-step" }) {
				page_header
				page_info
				medicaid_attestation_text
			}
		}
	`);

	const { existingFormData, handleFormSubmission, handleNavBack, handleNavNext } =
		useContext(FlowContext);

	const { memberInfo, loading } = useMemberInfo([], 'cache-first');

	useDeepCompareEffect(() => {
		if (loading || doesNotExist(memberInfo?.stateOfResidence?.abbreviation)) {
			return;
		}

		const isMedicaidState = includes(memberInfo?.stateOfResidence?.abbreviation, medicaidStates);
		if (!isMedicaidState) {
			props?.location?.state?.reverse ? handleNavBack(stepKey) : handleNavNext(stepKey);
		}
	}, [memberInfo, loading]);

	const refinedContent: IMedicaidAttestationStepPageContent = keysToCamel(
		pageContent
	) as IMedicaidAttestationStepPageContent;

	return (
		<LoadingWrapper loading={loading} skeleton={AttestationSkeleton}>
			<MedicaidAttestationStep
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				existingFormData={existingFormData}
				handleFormSubmission={handleFormSubmission}
				handleNavBack={handleNavBack}
				memberInfo={memberInfo}
			/>
		</LoadingWrapper>
	);
};

export default MedicaidAttestationStepPage;
